import React, { useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowRight, Briefcase, Shield, Compass } from "lucide-react";
import AdminLogo, { useBrandSettings } from "@/components/AdminLogo";
import { useEmployer } from "@/context/EmployerContext";
import { useAuth } from "@/context/AuthContext";

/**
 * Public "Portal" landing — a calm split screen letting visitors self-identify
 * as an Employer (main marketing / catalog experience) or an Admin
 * (protected CMS). A tertiary "Just exploring" option leads to the standard
 * home page for casual visitors.
 *
 * If the visitor is already authenticated (employer or admin), we bounce them
 * straight to their destination — respecting a `?next=` param from
 * `EmployerOnlyRoute` so deep-links land where the visitor originally aimed.
 */
export default function Portal() {
  const { t } = useTranslation();
  const { data: brand } = useBrandSettings();
  const companyName = brand?.company_name || "Adelina";
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next") || "";
  const { employer, loading: empLoading } = useEmployer();
  const { user, loading: adminLoading } = useAuth();

  useEffect(() => {
    if (empLoading || adminLoading) return;
    if (user) {
      navigate(next || "/admin", { replace: true });
    } else if (employer) {
      navigate(next || "/catalog", { replace: true });
    }
  }, [user, employer, empLoading, adminLoading, next, navigate]);

  const employerHref = next ? `/employer/login?next=${encodeURIComponent(next)}` : "/employer/login";
  const adminHref    = next ? `/admin/login?next=${encodeURIComponent(next)}`    : "/admin/login";

  return (
    <div className="min-h-screen bg-zinc-950 relative overflow-hidden grain">
      {/* Ambient gold orbs — matches admin auth aesthetic */}
      <div className="absolute -top-40 -right-40 w-[560px] h-[560px] rounded-full blur-3xl opacity-25 pointer-events-none"
           style={{ background: "radial-gradient(circle, #b8860b 0%, transparent 70%)" }} />
      <div className="absolute -bottom-40 -left-40 w-[520px] h-[520px] rounded-full blur-3xl opacity-20 pointer-events-none"
           style={{ background: "radial-gradient(circle, #d4a017 0%, transparent 70%)" }} />

      <div className="relative max-w-6xl mx-auto px-6 lg:px-10 pt-16 pb-24">
        {/* Brand */}
        <Link to="/" className="inline-flex items-center gap-3 group" data-testid="portal-brand-link">
          <AdminLogo size={48} variant="light" />
          <div className="leading-tight">
            <div className="font-serif text-xl text-white tracking-tight">{companyName}</div>
            <div className="text-[10px] tracking-[0.24em] uppercase text-zinc-500 font-medium">
              {t("brand.tagline", "Employment Agency")}
            </div>
          </div>
        </Link>

        {/* Header */}
        <div className="mt-16 max-w-2xl">
          <div className="text-[11px] tracking-[0.22em] uppercase text-[#d4a017] font-semibold mb-3" data-testid="portal-overline">
            {t("portal.overline", "Welcome")}
          </div>
          <h1 className="font-serif text-white text-4xl md:text-6xl tracking-tight leading-[1.05]">
            {t("portal.title", "Who are you today?")}
          </h1>
          <p className="mt-5 text-zinc-400 text-base md:text-lg leading-relaxed">
            {t("portal.subtitle", "Pick the door that fits your visit. You can always switch later.")}
          </p>
        </div>

        {/* Two primary cards */}
        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-6">
          <PortalCard
            to={employerHref}
            testId="portal-employer-card"
            icon={<Briefcase size={22} />}
            eyebrow={t("portal.employer_eyebrow", "For Employers")}
            title={t("portal.employer_title", "Browse candidates")}
            desc={t("portal.employer_desc", "Vetted, medically-cleared workers ready for placement across Brunei, Singapore, Malaysia & Taiwan. Book interviews directly.")}
            cta={t("portal.employer_cta", "Enter catalog")}
            primary
          />
          <PortalCard
            to={adminHref}
            testId="portal-admin-card"
            icon={<Shield size={22} />}
            eyebrow={t("portal.admin_eyebrow", "For Team Members")}
            title={t("portal.admin_title", "Admin sign-in")}
            desc={t("portal.admin_desc", "Access the internal CMS — candidate management, bookings, biodata schema, pipeline & analytics.")}
            cta={t("portal.admin_cta", "Sign in")}
          />
        </div>

        {/* Tertiary link */}
        <div className="mt-10 flex items-center gap-3 text-sm text-zinc-500">
          <Compass size={14} />
          <Link to="/" className="hover:text-[#d4a017] transition-colors underline-offset-4 hover:underline" data-testid="portal-explore-link">
            {t("portal.explore", "Just exploring — take me to the main site")}
          </Link>
        </div>
      </div>
    </div>
  );
}

function PortalCard({ to, testId, icon, eyebrow, title, desc, cta, primary = false }) {
  return (
    <Link
      to={to}
      data-testid={testId}
      className={`group relative block rounded-[28px] p-8 md:p-10 border transition-all duration-300 ${
        primary
          ? "bg-gradient-to-br from-[#b8860b] via-[#a06e08] to-[#8a5f07] border-[color:#d4a017]/50 shadow-2xl hover:shadow-[0_20px_60px_rgba(212,160,23,0.35)]"
          : "bg-zinc-900/70 backdrop-blur-xl border-zinc-800 hover:border-[color:#d4a017]/40 hover:bg-zinc-900/85"
      } hover:-translate-y-1`}
    >
      <div className={`inline-flex items-center gap-2 mb-6 ${primary ? "text-zinc-950" : "text-[#d4a017]"}`}>
        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${primary ? "bg-zinc-950 text-[#d4a017]" : "bg-[#d4a017]/10 border border-[#d4a017]/30"}`}>
          {icon}
        </div>
        <span className="text-[10.5px] tracking-[0.22em] uppercase font-semibold">{eyebrow}</span>
      </div>
      <h2 className={`font-serif text-3xl md:text-[38px] tracking-tight leading-tight ${primary ? "text-zinc-950" : "text-white"}`}>
        {title}
      </h2>
      <p className={`mt-4 text-[15px] leading-relaxed ${primary ? "text-zinc-900/80" : "text-zinc-400"}`}>
        {desc}
      </p>
      <div className={`mt-8 inline-flex items-center gap-2 text-sm font-semibold ${primary ? "text-zinc-950" : "text-[#d4a017]"}`}>
        {cta}
        <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
      </div>
    </Link>
  );
}
